/* eslint-disable no-restricted-syntax */
import {
  default as React,
  useCallback,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import * as XLSX from 'xlsx';
import { Content, Page, Progress } from '@backstage/core-components';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import type {
  OnboardingSummary,
  PendingPRsResponse,
  RecentlyOnboardedResponse,
  RemainingReposResponse,
} from '@internal/scaffolder-backend-module-catalog-onboarding';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BlockIcon from '@mui/icons-material/Block';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import RefreshIcon from '@mui/icons-material/Refresh';
import StorageIcon from '@mui/icons-material/Storage';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';

const CHART_COLORS = {
  onboarded: '#4caf50',
  prPending: '#ff9800',
  remaining: '#ffc107',
  inactive: '#ef5350',
  archived: '#9e9e9e',
  excluded: '#9c27b0',
};

const METHOD_COLORS = [
  '#2196f3',
  '#4caf50',
  '#ff9800',
  '#9c27b0',
  '#e91e63',
  '#00bcd4',
];

function buildRepoUrl(
  baseUrl: string,
  type: string,
  org: string,
  page: number,
  pageSize: number,
): string {
  if (type === 'remaining') {
    return `${baseUrl}/remaining-repos?org=${encodeURIComponent(
      org,
    )}&page=${page}&pageSize=${pageSize}`;
  }
  if (type === 'excluded') {
    return `${baseUrl}/excluded-repos?org=${encodeURIComponent(
      org,
    )}&page=${page}&pageSize=${pageSize}`;
  }
  return `${baseUrl}/activity-repos?org=${encodeURIComponent(
    org,
  )}&type=${type}&page=${page}&pageSize=${pageSize}`;
}

function pct(part: number, total: number): string {
  if (!total) return '0%';
  return `${Math.round((part / total) * 100)}%`;
}

function orgProgressColor(percentage: number): 'success' | 'warning' | 'error' {
  if (percentage >= 80) return 'success';
  if (percentage >= 50) return 'warning';
  return 'error';
}

function prRowBg(daysOpen: number): string | undefined {
  if (daysOpen >= 14) return 'error.50';
  if (daysOpen >= 7) return 'warning.50';
  return undefined;
}

function relativeTime(isoDate: string): string {
  const diff = Date.now() - new Date(isoDate).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months > 1 ? 's' : ''} ago`;
  const years = Math.floor(months / 12);
  return `${years} year${years > 1 ? 's' : ''} ago`;
}

function MetricCard({
  icon,
  label,
  count,
  total,
  color,
  loading,
  helpTooltip,
}: {
  icon: ReactNode;
  label: string;
  count: number;
  total: number;
  color: string;
  loading: boolean;
  helpTooltip?: ReactNode;
}) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ textAlign: 'center', py: 3 }}>
        <Box sx={{ color, mb: 1 }}>{icon}</Box>
        {loading ? (
          <>
            <Skeleton width="60%" sx={{ mx: 'auto' }} />
            <Skeleton width="40%" sx={{ mx: 'auto' }} />
          </>
        ) : (
          <>
            <Typography variant="h3" fontWeight="bold" sx={{ color }}>
              {count.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {pct(count, total)} of total
            </Typography>
          </>
        )}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          gap={0.5}
          sx={{ mt: 1 }}
        >
          <Typography variant="subtitle2">{label}</Typography>
          {helpTooltip && (
            <Tooltip title={helpTooltip} arrow placement="top">
              <InfoOutlinedIcon
                fontSize="small"
                sx={{ color: 'text.secondary', cursor: 'help' }}
              />
            </Tooltip>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

function PRAgeBadge({ daysOpen }: { daysOpen: number }) {
  if (daysOpen >= 14) {
    return (
      <Tooltip title={`${daysOpen} days open — overdue`}>
        <Box
          display="flex"
          alignItems="center"
          gap={0.5}
          sx={{ color: 'error.main' }}
        >
          <ErrorIcon fontSize="small" />
          <Typography variant="body2" color="error">
            {daysOpen}d
          </Typography>
        </Box>
      </Tooltip>
    );
  }
  if (daysOpen >= 7) {
    return (
      <Tooltip title={`${daysOpen} days open — getting stale`}>
        <Box
          display="flex"
          alignItems="center"
          gap={0.5}
          sx={{ color: 'warning.main' }}
        >
          <WarningIcon fontSize="small" />
          <Typography variant="body2" color="warning.main">
            {daysOpen}d
          </Typography>
        </Box>
      </Tooltip>
    );
  }
  return <Typography variant="body2">{daysOpen}d</Typography>;
}

export function OnboardingTrackerPage() {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  const [summary, setSummary] = useState<OnboardingSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  const [prs, setPrs] = useState<PendingPRsResponse | null>(null);
  const [prsLoading, setPrsLoading] = useState(true);
  const [prsPage, setPrsPage] = useState(0);

  const [recentlyOnboarded, setRecentlyOnboarded] =
    useState<RecentlyOnboardedResponse | null>(null);
  const [recentlyOnboardedLoading, setRecentlyOnboardedLoading] =
    useState(true);
  const [recentlyOnboardedPage, setRecentlyOnboardedPage] = useState(0);

  type OrgDrawerType = 'remaining' | 'inactive' | 'low-activity' | 'excluded';
  const [orgDrawer, setOrgDrawer] = useState<{
    org: string;
    type: OrgDrawerType;
  } | null>(null);
  const [remainingRepos, setRemainingRepos] =
    useState<RemainingReposResponse | null>(null);
  const [remainingLoading, setRemainingLoading] = useState(false);
  const [remainingPage, setRemainingPage] = useState(0);

  const [orgFilter, setOrgFilter] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [rateLimitWarning, setRateLimitWarning] = useState(false);

  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('onboarding-tracker');
      const res = await fetchApi.fetch(`${baseUrl}/summary`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const remaining = res.headers.get('X-GitHub-RateLimit-Remaining');
      if (remaining !== null && parseInt(remaining, 10) < 200) {
        setRateLimitWarning(true);
      }
      const data: OnboardingSummary = await res.json();
      setSummary(data);
    } catch (err: any) {
      setSummaryError(err.message ?? 'Failed to load summary');
    } finally {
      setSummaryLoading(false);
    }
  }, [fetchApi, discoveryApi]);

  const fetchPRs = useCallback(
    async (page: number) => {
      setPrsLoading(true);
      try {
        const baseUrl = await discoveryApi.getBaseUrl('onboarding-tracker');
        const res = await fetchApi.fetch(
          `${baseUrl}/pending-prs?page=${page + 1}&pageSize=10`,
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data: PendingPRsResponse = await res.json();
        setPrs(data);
      } catch {
        // keep previous data
      } finally {
        setPrsLoading(false);
      }
    },
    [fetchApi, discoveryApi],
  );

  const fetchRecentlyOnboarded = useCallback(
    async (page: number) => {
      setRecentlyOnboardedLoading(true);
      try {
        const baseUrl = await discoveryApi.getBaseUrl('onboarding-tracker');
        const res = await fetchApi.fetch(
          `${baseUrl}/recently-onboarded?page=${page + 1}&pageSize=10`,
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data: RecentlyOnboardedResponse = await res.json();
        setRecentlyOnboarded(data);
      } catch {
        // keep previous data
      } finally {
        setRecentlyOnboardedLoading(false);
      }
    },
    [fetchApi, discoveryApi],
  );

  const fetchOrgDrawerRepos = useCallback(
    async (org: string, type: OrgDrawerType, page: number) => {
      setRemainingLoading(true);
      try {
        const baseUrl = await discoveryApi.getBaseUrl('onboarding-tracker');
        const url = buildRepoUrl(baseUrl, type, org, page + 1, 50);
        const res = await fetchApi.fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data: RemainingReposResponse = await res.json();
        setRemainingRepos(data);
      } catch {
        // keep previous data on error
      } finally {
        setRemainingLoading(false);
      }
    },
    [fetchApi, discoveryApi],
  );

  const openOrgDrawer = useCallback(
    (org: string, type: OrgDrawerType) => {
      setOrgDrawer({ org, type });
      setRemainingPage(0);
      setRemainingRepos(null);
      fetchOrgDrawerRepos(org, type, 0);
    },
    [fetchOrgDrawerRepos],
  );

  const closeRemainingDrawer = useCallback(() => {
    setOrgDrawer(null);
    setRemainingRepos(null);
    setRemainingPage(0);
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchPRs(prsPage);
  }, [fetchPRs, prsPage]);

  useEffect(() => {
    fetchRecentlyOnboarded(recentlyOnboardedPage);
  }, [fetchRecentlyOnboarded, recentlyOnboardedPage]);

  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [refreshCooldownSecs, setRefreshCooldownSecs] = useState(0);
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const [downloadingGlobal, setDownloadingGlobal] = useState<
    'remaining' | 'inactive' | 'excluded' | null
  >(null);

  const handleDownloadCsv = useCallback(async () => {
    if (!orgDrawer) return;
    setDownloadingCsv(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('onboarding-tracker');
      const pageSize = 200;
      const allItems: RemainingReposResponse['items'] = [];
      let page = 1;
      let total = 0;
      do {
        const url = buildRepoUrl(
          baseUrl,
          orgDrawer.type,
          orgDrawer.org,
          page,
          pageSize,
        );
        const res = await fetchApi.fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data: RemainingReposResponse = await res.json();
        total = data.total;
        allItems.push(...data.items);
        page++;
      } while (allItems.length < total);
      const data = { items: allItems, total };

      const isExcluded = orgDrawer.type === 'excluded';
      const header = isExcluded
        ? ['Repository', 'GitHub URL', 'Last Pushed', 'Reason', 'Topics']
        : ['Repository', 'GitHub URL', 'Last Pushed', 'Topics'];
      const rows = data.items.map(repo => {
        const pushedAtStr = repo.pushedAt
          ? new Date(repo.pushedAt).toLocaleDateString()
          : '';
        return isExcluded
          ? [
              repo.name,
              repo.githubUrl,
              pushedAtStr,
              repo.reason ?? '',
              repo.topics.join('; '),
            ]
          : [repo.name, repo.githubUrl, pushedAtStr, repo.topics.join('; ')];
      });

      const csvContent = [header, ...rows]
        .map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(','))
        .join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      const fileNameMap: Record<OrgDrawerType, string> = {
        remaining: 'remaining-repos',
        inactive: 'inactive-repos',
        'low-activity': 'low-activity-repos',
        excluded: 'excluded-repos',
      };
      link.download = `${orgDrawer.org}-${fileNameMap[orgDrawer.type]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch {
      // silently ignore download errors
    } finally {
      setDownloadingCsv(false);
    }
  }, [orgDrawer, fetchApi, discoveryApi]);

  type GlobalDownloadType = 'remaining' | 'inactive' | 'excluded';

  const handleGlobalDownload = useCallback(
    async (type: GlobalDownloadType) => {
      if (!summary) return;
      setDownloadingGlobal(type);
      try {
        const baseUrl = await discoveryApi.getBaseUrl('onboarding-tracker');
        const pageSize = 200;

        const orgsWithData = summary.byOrg.filter(o => {
          if (type === 'remaining') return o.remaining > 0;
          if (type === 'inactive') return o.inactiveRepos > 0;
          return o.excluded + o.archived > 0;
        });

        const allRows = await Promise.all(
          orgsWithData.map(async orgData => {
            const items: RemainingReposResponse['items'] = [];
            let page = 1;
            let total = 0;
            do {
              const url = buildRepoUrl(
                baseUrl,
                type,
                orgData.org,
                page,
                pageSize,
              );
              const res = await fetchApi.fetch(url);
              if (!res.ok) throw new Error(`HTTP ${res.status}`);
              const data: RemainingReposResponse = await res.json();
              total = data.total;
              items.push(...data.items);
              page++;
            } while (items.length < total);
            return items.map(item => ({ ...item, orgName: orgData.org }));
          }),
        );

        const workbook = XLSX.utils.book_new();

        const isExcluded = type === 'excluded';
        const header = isExcluded
          ? ['Repository', 'GitHub URL', 'Last Pushed', 'Reason', 'Topics']
          : ['Repository', 'GitHub URL', 'Last Pushed', 'Topics'];

        orgsWithData.forEach((orgData, idx) => {
          const orgItems = allRows[idx];
          const sheetData = [
            header,
            ...orgItems.map(repo =>
              isExcluded
                ? [
                    repo.name,
                    repo.githubUrl,
                    repo.pushedAt
                      ? new Date(repo.pushedAt).toLocaleDateString()
                      : '',
                    repo.reason ?? '',
                    repo.topics.join('; '),
                  ]
                : [
                    repo.name,
                    repo.githubUrl,
                    new Date(repo.pushedAt).toLocaleDateString(),
                    repo.topics.join('; '),
                  ],
            ),
          ];
          const sheet = XLSX.utils.aoa_to_sheet(sheetData);
          // Auto-fit column widths based on max content length in each column
          const colWidths = sheetData.reduce<number[]>((widths, row) => {
            row.forEach((cell, i) => {
              widths[i] = Math.max(
                widths[i] ?? 10,
                String(cell ?? '').length + 2,
              );
            });
            return widths;
          }, []);
          sheet['!cols'] = colWidths.map(w => ({ wch: Math.min(w, 80) }));
          const sheetName = orgData.org.slice(0, 31);
          XLSX.utils.book_append_sheet(workbook, sheet, sheetName);
        });

        const labelMap: Record<GlobalDownloadType, string> = {
          remaining: 'remaining-repos',
          inactive: 'inactive-repos',
          excluded: 'excluded-repos',
        };
        XLSX.writeFile(workbook, `all-orgs-${labelMap[type]}.xlsx`);
      } catch {
        // silently ignore download errors
      } finally {
        setDownloadingGlobal(null);
      }
    },
    [summary, fetchApi, discoveryApi],
  );

  // Tick the cooldown counter down every second
  useEffect(() => {
    if (refreshCooldownSecs <= 0) return undefined;
    const id = setTimeout(() => setRefreshCooldownSecs(s => s - 1), 1000);
    return () => clearTimeout(id);
  }, [refreshCooldownSecs]);

  const handleRefresh = async () => {
    setRefreshing(true);
    setRefreshError(null);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('onboarding-tracker');
      const res = await fetchApi.fetch(`${baseUrl}/refresh`, {
        method: 'POST',
      });
      if (res.status === 429) {
        const body = await res.json();
        const retryAfter = parseInt(res.headers.get('Retry-After') ?? '', 10);
        setRefreshCooldownSecs(isNaN(retryAfter) ? 600 : retryAfter);
        setRefreshError(body.message ?? 'Please wait before refreshing again');
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setRefreshError(
          (body as { message?: string }).message ??
            `Refresh failed (HTTP ${res.status})`,
        );
        return;
      }
      // Successful refresh — enforce 10-min client-side cooldown
      setRefreshCooldownSecs(600);
      await fetchSummary();
      await fetchPRs(0);
      await fetchRecentlyOnboarded(0);
      setRecentlyOnboardedPage(0);
      setPrsPage(0);
    } finally {
      setRefreshing(false);
    }
  };

  const donutData = summary
    ? [
        {
          name: 'Onboarded',
          value: summary.overall.onboarded,
          color: CHART_COLORS.onboarded,
        },
        {
          name: 'PR Pending',
          value: summary.overall.prPending,
          color: CHART_COLORS.prPending,
        },
        {
          name: 'Remaining',
          value: summary.overall.remaining,
          color: CHART_COLORS.remaining,
        },
        {
          name: 'Inactive',
          value: summary.overall.inactiveRepos,
          color: CHART_COLORS.inactive,
        },
        {
          name: 'Excluded',
          value: summary.overall.excluded + summary.overall.archived,
          color: CHART_COLORS.excluded,
        },
      ]
    : [];

  const methodData = summary
    ? Object.entries(summary.methodBreakdown)
        .sort((a, b) => b[1] - a[1])
        .map(([name, value]) => ({ name, value }))
    : [];

  const filteredOrgs = summary
    ? summary.byOrg.filter(o =>
        o.org.toLowerCase().includes(orgFilter.toLowerCase()),
      )
    : [];

  const total = summary?.overall.total ?? 0;

  const methodBreakdownContent = (() => {
    if (summaryLoading) return <Skeleton variant="rectangular" height={220} />;
    if (methodData.length === 0) {
      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height={220}
        >
          <Typography color="text.secondary">
            No method data available
          </Typography>
        </Box>
      );
    }
    return (
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={methodData} layout="vertical">
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" />
          <YAxis type="category" dataKey="name" width={100} />
          <RechartsTooltip
            formatter={
              ((value: number) => [
                `${value.toLocaleString()} (${pct(
                  value,
                  summary?.overall.onboarded ?? 0,
                )})`,
                'Repos',
              ]) as any
            }
          />
          <Bar dataKey="value" radius={[0, 4, 4, 0]}>
            {methodData.map((_, index) => (
              <Cell
                key={index}
                fill={METHOD_COLORS[index % METHOD_COLORS.length]}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  })();

  const pendingPRsContent = (() => {
    if (prsLoading) return <Progress />;
    if (prs?.total === 0) {
      return (
        <Box py={4} textAlign="center">
          <CheckCircleOutlineIcon
            sx={{ fontSize: 48, color: 'success.main', mb: 1 }}
          />
          <Typography variant="h6" color="success.main">
            No pending PRs — great work! 🎉
          </Typography>
        </Box>
      );
    }
    return (
      <>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Repository</strong>
                </TableCell>
                <TableCell>
                  <strong>PR Title</strong>
                </TableCell>
                <TableCell>
                  <strong>Organization</strong>
                </TableCell>
                <TableCell>
                  <strong>Author</strong>
                </TableCell>
                <TableCell>
                  <strong>Created</strong>
                </TableCell>
                <TableCell>
                  <strong>Age</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(prs?.items ?? []).map(pr => {
                return (
                  <TableRow
                    key={pr.prUrl}
                    hover
                    sx={{ bgcolor: prRowBg(pr.daysOpen) }}
                  >
                    <TableCell>
                      <Link
                        href={pr.githubUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                      >
                        {pr.repoName}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Link
                        href={pr.prUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                      >
                        #{pr.prNumber} {pr.prTitle}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Chip label={pr.org} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{pr.author}</Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={new Date(pr.createdAt).toLocaleString()}>
                        <Typography variant="body2">
                          {relativeTime(pr.createdAt)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <PRAgeBadge daysOpen={pr.daysOpen} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={prs?.total ?? 0}
          page={prsPage}
          onPageChange={(_, p) => setPrsPage(p)}
          rowsPerPage={10}
          rowsPerPageOptions={[10]}
        />
      </>
    );
  })();

  return (
    <Page themeId="tool">
      <Content>
        {rateLimitWarning && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            onClose={() => setRateLimitWarning(false)}
          >
            GitHub API rate limit is low — showing cached data. Data may be up
            to 15 minutes old.
          </Alert>
        )}
        {refreshError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setRefreshError(null)}
          >
            {refreshError}
          </Alert>
        )}

        {/* Header */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
        >
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Repository Onboarding Tracker
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {summary
                ? `Last refreshed ${relativeTime(summary.lastRefreshed)}`
                : 'Loading data from GitHub...'}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            {(
              [
                { type: 'remaining', label: 'Remaining Excel' },
                { type: 'inactive', label: 'Inactive Excel' },
                { type: 'excluded', label: 'Excluded Excel' },
              ] as {
                type: 'remaining' | 'inactive' | 'excluded';
                label: string;
              }[]
            ).map(({ type, label }) => (
              <Button
                key={type}
                size="small"
                variant="outlined"
                startIcon={
                  downloadingGlobal === type ? (
                    <CircularProgress size={14} />
                  ) : (
                    <DownloadIcon />
                  )
                }
                onClick={() => handleGlobalDownload(type)}
                disabled={
                  summaryLoading || downloadingGlobal !== null || !summary
                }
              >
                {downloadingGlobal === type ? 'Downloading…' : label}
              </Button>
            ))}
            <Tooltip
              title={
                refreshCooldownSecs > 0
                  ? `Available in ${Math.floor(refreshCooldownSecs / 60)}m ${
                      refreshCooldownSecs % 60
                    }s`
                  : ''
              }
              disableHoverListener={refreshCooldownSecs === 0}
            >
              <span>
                <Button
                  variant="outlined"
                  startIcon={
                    refreshing ? (
                      <CircularProgress size={16} />
                    ) : (
                      <RefreshIcon />
                    )
                  }
                  onClick={handleRefresh}
                  disabled={
                    refreshing || summaryLoading || refreshCooldownSecs > 0
                  }
                >
                  {refreshing && 'Refreshing...'}
                  {!refreshing &&
                    refreshCooldownSecs > 0 &&
                    `Refresh (${Math.floor(refreshCooldownSecs / 60)}m ${
                      refreshCooldownSecs % 60
                    }s)`}
                  {!refreshing && refreshCooldownSecs === 0 && 'Refresh'}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {summaryError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {summaryError}
          </Alert>
        )}

        {/* Hero Metrics */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6} md={2}>
            <MetricCard
              icon={<StorageIcon fontSize="large" />}
              label="Total Repos"
              count={summary?.overall.total ?? 0}
              total={total}
              color="#1976d2"
              loading={summaryLoading}
              helpTooltip="All repos across all orgs — Onboarded + PR Pending + Remaining + Inactive + Excluded"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <MetricCard
              icon={<CheckCircleOutlineIcon fontSize="large" />}
              label="Onboarded"
              count={summary?.overall.onboarded ?? 0}
              total={total}
              color={CHART_COLORS.onboarded}
              loading={summaryLoading}
              helpTooltip="Repos with a backstage.yaml registered in the SkyStage catalog"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <MetricCard
              icon={<PendingActionsIcon fontSize="large" />}
              label="PR Pending"
              count={summary?.overall.prPending ?? 0}
              total={total}
              color={CHART_COLORS.prPending}
              loading={summaryLoading}
              helpTooltip="Repos with an open onboarding PR not yet merged — onboarding in progress"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <MetricCard
              icon={<HourglassEmptyIcon fontSize="large" />}
              label="Remaining"
              count={summary?.overall.remaining ?? 0}
              total={total}
              color={CHART_COLORS.remaining}
              loading={summaryLoading}
              helpTooltip={
                <Box sx={{ textAlign: 'left', p: 0.5 }}>
                  <Typography
                    variant="caption"
                    display="block"
                    fontWeight="bold"
                    mb={0.5}
                  >
                    Active repos still needing onboarding:
                  </Typography>
                  <Typography variant="caption" display="block">
                    ✓ Had a push in the last 90 days
                  </Typography>
                  <Typography variant="caption" display="block">
                    ✓ Not yet onboarded (no backstage.yaml)
                  </Typography>
                  <Typography variant="caption" display="block">
                    ✓ No open onboarding PR
                  </Typography>
                  <Typography variant="caption" display="block" mt={0.5}>
                    Excludes archived, excluded, and inactive repos
                  </Typography>
                </Box>
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <MetricCard
              icon={<PauseCircleOutlineIcon fontSize="large" />}
              label="Inactive"
              count={summary?.overall.inactiveRepos ?? 0}
              total={total}
              color={CHART_COLORS.inactive}
              loading={summaryLoading}
              helpTooltip="Repos with no push in 90+ days — not actionable, not counted in Remaining"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <MetricCard
              icon={<BlockIcon fontSize="large" />}
              label="Excluded"
              count={
                (summary?.overall.excluded ?? 0) +
                (summary?.overall.archived ?? 0)
              }
              total={total}
              color={CHART_COLORS.excluded}
              loading={summaryLoading}
              helpTooltip={
                <Box sx={{ textAlign: 'left', p: 0.5 }}>
                  <Typography
                    variant="caption"
                    display="block"
                    fontWeight="bold"
                    mb={0.5}
                  >
                    Not counted in any metric (archived + rule-excluded):
                  </Typography>
                  <Typography variant="caption" display="block">
                    • Archived on GitHub
                  </Typography>
                  <Typography variant="caption" display="block">
                    • Prefix:{' '}
                    {summary?.exclusionCriteria?.prefixes.join(', ') ??
                      'test-, playground-, sandbox-'}
                  </Typography>
                  <Typography variant="caption" display="block">
                    • Name contains:{' '}
                    {summary?.exclusionCriteria?.nameContains.join(', ') ??
                      'terraform'}
                  </Typography>
                  <Typography variant="caption" display="block">
                    • Topic:{' '}
                    {summary?.exclusionCriteria?.topics.join(', ') ??
                      'skystage-exclude, terraform'}
                  </Typography>
                </Box>
              }
            />
          </Grid>
        </Grid>

        {/* Charts Row */}
        <Grid container spacing={2} mb={3}>
          {/* Donut Chart */}
          <Grid item xs={12} md={5}>
            <Card sx={{ height: 320 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Onboarding Breakdown
                </Typography>
                {summaryLoading ? (
                  <Skeleton
                    variant="circular"
                    width={200}
                    height={200}
                    sx={{ mx: 'auto' }}
                  />
                ) : (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={donutData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {donutData.map((entry, index) => (
                          <Cell key={index} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip
                        formatter={
                          ((value: number, name: string) => [
                            `${value.toLocaleString()} (${pct(value, total)})`,
                            name,
                          ]) as any
                        }
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Method Breakdown */}
          <Grid item xs={12} md={7}>
            <Card sx={{ height: 320 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Onboarding Method Breakdown
                </Typography>
                {methodBreakdownContent}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Org-wise Breakdown */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={2}
            >
              <Typography variant="h6">Org-wise Breakdown</Typography>
              <TextField
                size="small"
                placeholder="Filter by org..."
                value={orgFilter}
                onChange={e => setOrgFilter(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 220 }}
              />
            </Box>
            {summaryLoading ? (
              <Progress />
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>Organization</strong>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip
                          title="Total repos in this org (all types, including archived & excluded)"
                          arrow
                          componentsProps={{
                            tooltip: { sx: { maxWidth: 240 } },
                          }}
                        >
                          <Box
                            display="inline-flex"
                            alignItems="center"
                            gap={0.5}
                            sx={{ cursor: 'help' }}
                          >
                            <strong>Total</strong>
                            <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip
                          title="Repos with a push in the last 30 days"
                          arrow
                          componentsProps={{
                            tooltip: { sx: { maxWidth: 220 } },
                          }}
                        >
                          <Box
                            display="inline-flex"
                            alignItems="center"
                            gap={0.5}
                            sx={{ cursor: 'help' }}
                          >
                            <strong>Active</strong>
                            <InfoOutlinedIcon
                              sx={{ fontSize: 14, color: 'success.main' }}
                            />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip
                          title="Repos whose last push was 30–90 days ago — still alive but slowing down"
                          arrow
                          placement="bottom"
                          componentsProps={{
                            tooltip: { sx: { maxWidth: 300 } },
                          }}
                        >
                          <Box
                            display="inline-flex"
                            alignItems="center"
                            gap={0.5}
                            sx={{ cursor: 'help' }}
                          >
                            <strong>Low Activity</strong>
                            <InfoOutlinedIcon
                              sx={{ fontSize: 14, color: 'warning.main' }}
                            />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip
                          title="Repos with a backstage.yaml registered in the catalog"
                          arrow
                          componentsProps={{
                            tooltip: { sx: { maxWidth: 220 } },
                          }}
                        >
                          <Box
                            display="inline-flex"
                            alignItems="center"
                            gap={0.5}
                            sx={{ cursor: 'help' }}
                          >
                            <strong>Onboarded</strong>
                            <InfoOutlinedIcon
                              sx={{ fontSize: 14, color: 'success.main' }}
                            />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip
                          title="Repos with an open onboarding PR not yet merged"
                          arrow
                          componentsProps={{
                            tooltip: { sx: { maxWidth: 220 } },
                          }}
                        >
                          <Box
                            display="inline-flex"
                            alignItems="center"
                            gap={0.5}
                            sx={{ cursor: 'help' }}
                          >
                            <strong>PR Pending</strong>
                            <InfoOutlinedIcon
                              sx={{ fontSize: 14, color: 'warning.main' }}
                            />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip
                          arrow
                          placement="bottom"
                          componentsProps={{
                            tooltip: { sx: { maxWidth: 320 } },
                          }}
                          title={
                            <Box sx={{ textAlign: 'left', p: 0.5 }}>
                              <Typography
                                variant="caption"
                                display="block"
                                fontWeight="bold"
                                mb={0.5}
                              >
                                Repos still needing onboarding — counts repos
                                that are:
                              </Typography>
                              <Typography variant="caption" display="block">
                                ✓ Had a push in the last 90 days (active)
                              </Typography>
                              <Typography variant="caption" display="block">
                                ✓ Not yet onboarded (no backstage.yaml)
                              </Typography>
                              <Typography variant="caption" display="block">
                                ✓ No open onboarding PR
                              </Typography>
                              <Typography
                                variant="caption"
                                display="block"
                                mt={0.5}
                              >
                                Does not count archived, excluded, or inactive
                                repos
                              </Typography>
                            </Box>
                          }
                        >
                          <Box
                            display="inline-flex"
                            alignItems="center"
                            gap={0.5}
                            sx={{ cursor: 'help' }}
                          >
                            <strong>Remaining</strong>
                            <InfoOutlinedIcon
                              sx={{ fontSize: 14, color: 'error.main' }}
                            />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip
                          title="No push in 90+ days — not actionable, excluded from Remaining"
                          arrow
                          placement="bottom"
                          componentsProps={{
                            tooltip: { sx: { maxWidth: 300 } },
                          }}
                        >
                          <Box
                            display="inline-flex"
                            alignItems="center"
                            gap={0.5}
                            sx={{ cursor: 'help' }}
                          >
                            <strong>Inactive</strong>
                            <InfoOutlinedIcon
                              sx={{ fontSize: 14, color: 'error.main' }}
                            />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip
                          arrow
                          placement="bottom"
                          componentsProps={{
                            tooltip: { sx: { maxWidth: 320 } },
                          }}
                          title={
                            <Box sx={{ textAlign: 'left', p: 0.5 }}>
                              <Typography
                                variant="caption"
                                display="block"
                                fontWeight="bold"
                                mb={0.5}
                              >
                                Not counted in any metric (archived +
                                rule-excluded):
                              </Typography>
                              <Typography variant="caption" display="block">
                                • Archived on GitHub
                              </Typography>
                              <Typography variant="caption" display="block">
                                • Prefix:{' '}
                                {(
                                  summary?.exclusionCriteria?.prefixes ?? [
                                    'test-',
                                    'playground-',
                                    'sandbox-',
                                  ]
                                ).join(', ')}
                              </Typography>
                              <Typography variant="caption" display="block">
                                • Name contains:{' '}
                                {(
                                  summary?.exclusionCriteria?.nameContains ?? [
                                    'terraform',
                                  ]
                                ).join(', ')}
                              </Typography>
                              <Typography variant="caption" display="block">
                                • Topic:{' '}
                                {(
                                  summary?.exclusionCriteria?.topics ?? [
                                    'skystage-exclude',
                                    'terraform',
                                  ]
                                ).join(', ')}
                              </Typography>
                            </Box>
                          }
                        >
                          <Box
                            display="inline-flex"
                            alignItems="center"
                            gap={0.5}
                            sx={{ cursor: 'help' }}
                          >
                            <strong>Excluded</strong>
                            <InfoOutlinedIcon
                              sx={{ fontSize: 14, color: 'error.main' }}
                            />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ minWidth: 160 }}>
                        <strong>Progress</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredOrgs.map(org => {
                      const activeTotal =
                        org.onboarded + org.prPending + org.remaining;
                      const pctNum =
                        activeTotal > 0
                          ? Math.round((org.onboarded / activeTotal) * 100)
                          : 0;
                      const barColor = orgProgressColor(pctNum);
                      const excludedTotal = org.excluded + org.archived;
                      return (
                        <TableRow key={org.org} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {org.org}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {org.total.toLocaleString()}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ color: 'success.main', fontWeight: 'medium' }}
                          >
                            {org.activeRepos.toLocaleString()}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            {org.atRiskRepos > 0 ? (
                              <Link
                                component="button"
                                variant="body2"
                                underline="always"
                                onClick={() =>
                                  openOrgDrawer(org.org, 'low-activity')
                                }
                                sx={{
                                  cursor: 'pointer',
                                  color: 'warning.main',
                                  fontWeight: 'medium',
                                }}
                              >
                                {org.atRiskRepos.toLocaleString()}
                              </Link>
                            ) : (
                              <Typography variant="body2" color="text.disabled">
                                0
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ color: 'success.main' }}
                          >
                            {org.onboarded.toLocaleString()}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ color: 'warning.main' }}
                          >
                            {org.prPending.toLocaleString()}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            {org.remaining > 0 ? (
                              <Link
                                component="button"
                                variant="body2"
                                fontWeight="bold"
                                underline="always"
                                onClick={() =>
                                  openOrgDrawer(org.org, 'remaining')
                                }
                                sx={{ cursor: 'pointer' }}
                              >
                                {org.remaining.toLocaleString()}
                              </Link>
                            ) : (
                              <Typography variant="body2" fontWeight="bold">
                                0
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            {org.inactiveRepos > 0 ? (
                              <Link
                                component="button"
                                variant="body2"
                                underline="always"
                                onClick={() =>
                                  openOrgDrawer(org.org, 'inactive')
                                }
                                sx={{
                                  cursor: 'pointer',
                                  color: 'error.main',
                                  fontWeight: 'bold',
                                }}
                              >
                                {org.inactiveRepos.toLocaleString()}
                              </Link>
                            ) : (
                              <Typography variant="body2" color="text.disabled">
                                0
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            {excludedTotal > 0 ? (
                              <Link
                                component="button"
                                variant="body2"
                                underline="always"
                                onClick={() =>
                                  openOrgDrawer(org.org, 'excluded')
                                }
                                sx={{
                                  cursor: 'pointer',
                                  color: 'error.main',
                                  fontWeight: 'bold',
                                }}
                              >
                                {excludedTotal.toLocaleString()}
                              </Link>
                            ) : (
                              <Typography variant="body2" color="text.disabled">
                                0
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Box flex={1}>
                                <LinearProgress
                                  variant="determinate"
                                  value={pctNum}
                                  color={barColor}
                                  sx={{ height: 8, borderRadius: 4 }}
                                />
                              </Box>
                              <Typography
                                variant="caption"
                                sx={{ minWidth: 36 }}
                              >
                                {pctNum}%
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {filteredOrgs.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={10} align="center">
                          <Typography color="text.secondary">
                            No organizations found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Recently Onboarded */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <MergeTypeIcon sx={{ color: 'success.main' }} />
              <Typography variant="h6">
                Recently Onboarded (Last 30 Days)
              </Typography>
              {recentlyOnboarded && (
                <Chip
                  label={recentlyOnboarded.total}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              )}
            </Box>
            {recentlyOnboardedLoading && <Progress />}
            {!recentlyOnboardedLoading && recentlyOnboarded?.total === 0 && (
              <Box py={3} textAlign="center">
                <Typography color="text.secondary">
                  No repos onboarded via merged PRs in the last 30 days
                </Typography>
              </Box>
            )}
            {!recentlyOnboardedLoading &&
              (recentlyOnboarded?.total ?? 0) > 0 && (
                <>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <strong>Repository</strong>
                          </TableCell>
                          <TableCell>
                            <strong>PR Title</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Organization</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Author</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Merged</strong>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(recentlyOnboarded?.items ?? []).map(repo => (
                          <TableRow key={repo.prUrl} hover>
                            <TableCell>
                              <Link
                                href={repo.githubUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="hover"
                              >
                                {repo.repoName}
                              </Link>
                            </TableCell>
                            <TableCell>
                              <Link
                                href={repo.prUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="hover"
                              >
                                #{repo.prNumber} {repo.prTitle}
                              </Link>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={repo.org}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {repo.author}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Tooltip
                                title={new Date(repo.mergedAt).toLocaleString()}
                              >
                                <Typography variant="body2">
                                  {relativeTime(repo.mergedAt)}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={recentlyOnboarded?.total ?? 0}
                    page={recentlyOnboardedPage}
                    onPageChange={(_e, p) => setRecentlyOnboardedPage(p)}
                    rowsPerPage={10}
                    rowsPerPageOptions={[10]}
                  />
                </>
              )}
          </CardContent>
        </Card>

        {/* Pending PRs */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Pending Onboarding PRs
            </Typography>
            {pendingPRsContent}
          </CardContent>
        </Card>
      </Content>

      {/* Org repo drill-down drawer (remaining / inactive / low-activity) */}
      {(() => {
        const drawerTitles: Record<OrgDrawerType, string> = {
          remaining: 'Remaining Repos',
          inactive: 'Inactive Repos',
          'low-activity': 'Low Activity Repos',
          excluded: 'Excluded Repos',
        };
        const drawerSubtitles: Record<OrgDrawerType, string> = {
          remaining: 'not yet onboarded, pushed within last 90 days',
          inactive: 'no push in over 90 days',
          'low-activity': 'last push was 30–90 days ago',
          excluded: 'archived or rule-excluded from onboarding',
        };
        const emptyMessages: Record<OrgDrawerType, string> = {
          remaining: 'No remaining repos — all active repos are onboarded!',
          inactive: 'No inactive repos in this org.',
          'low-activity': 'No low-activity repos in this org.',
          excluded: 'No excluded repos in this org.',
        };
        const drawerType = orgDrawer?.type ?? 'remaining';
        return (
          <Drawer
            anchor="right"
            open={Boolean(orgDrawer)}
            onClose={closeRemainingDrawer}
            PaperProps={{ sx: { width: { xs: '100%', sm: 600 } } }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              px={3}
              py={2}
            >
              <Box>
                <Typography variant="h6">{orgDrawer?.org ?? ''}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {drawerTitles[drawerType]} — {drawerSubtitles[drawerType]}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={
                    downloadingCsv ? (
                      <CircularProgress size={14} />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  onClick={handleDownloadCsv}
                  disabled={
                    downloadingCsv ||
                    remainingLoading ||
                    (remainingRepos?.total ?? 0) === 0
                  }
                >
                  {downloadingCsv ? 'Downloading…' : 'Download CSV'}
                </Button>
                <IconButton onClick={closeRemainingDrawer} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>
            <Divider />

            {remainingLoading && <Progress />}

            {!remainingLoading && remainingRepos?.total === 0 && (
              <Box py={4} textAlign="center">
                <Typography color="text.secondary">
                  {emptyMessages[drawerType]}
                </Typography>
              </Box>
            )}

            {!remainingLoading && (remainingRepos?.total ?? 0) > 0 && (
              <>
                <TableContainer sx={{ flex: 1 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>Repository</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Last Pushed</strong>
                        </TableCell>
                        {drawerType === 'excluded' && (
                          <TableCell>
                            <strong>Reason</strong>
                          </TableCell>
                        )}
                        <TableCell>
                          <strong>Topics</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(remainingRepos?.items ?? []).map(repo => (
                        <TableRow key={repo.fullName} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <Link
                              href={repo.githubUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                            >
                              {repo.name}
                            </Link>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {repo.pushedAt ? (
                              <Tooltip
                                title={new Date(repo.pushedAt).toLocaleString()}
                              >
                                <Typography variant="body2">
                                  {repo.daysSincePush === 0
                                    ? 'today'
                                    : `${repo.daysSincePush}d ago`}
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Typography variant="body2" color="text.disabled">
                                —
                              </Typography>
                            )}
                          </TableCell>
                          {drawerType === 'excluded' && (
                            <TableCell>
                              <Chip
                                label={repo.reason ?? '—'}
                                size="small"
                                variant="outlined"
                                color={
                                  repo.reason === 'Archived'
                                    ? 'default'
                                    : 'error'
                                }
                              />
                            </TableCell>
                          )}
                          <TableCell>
                            <Box display="flex" gap={0.5} flexWrap="wrap">
                              {repo.topics.slice(0, 3).map(t => (
                                <Chip
                                  key={t}
                                  label={t}
                                  size="small"
                                  variant="outlined"
                                />
                              ))}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={remainingRepos?.total ?? 0}
                  page={remainingPage}
                  onPageChange={(_e, p) => {
                    if (!orgDrawer) return;
                    setRemainingPage(p);
                    fetchOrgDrawerRepos(orgDrawer.org, orgDrawer.type, p);
                  }}
                  rowsPerPage={50}
                  rowsPerPageOptions={[50]}
                />
              </>
            )}
          </Drawer>
        );
      })()}
    </Page>
  );
}
